import { gsap, ScrollTrigger, EASE_OUT } from "@/lib/motion/gsap";
import type { SiteMotion } from "@/lib/motion/site-runtime";

/** Homepage spine sections (ADR-005 order after hero). Shared by resize restore + `?section=`. */
export const SPINE_SECTIONS = [
  "hero",
  "work",
  "services",
  "products",
  "story",
  "contact",
] as const;

export type SpineSection = (typeof SPINE_SECTIONS)[number];

/** sessionStorage key — one-shot across ADR-018 width reload (EN `/` + AR `/ar`). */
export const SPINE_SECTION_STORAGE_KEY = "xattir:spine-section";

export function isSpineSection(value: unknown): value is SpineSection {
  return (
    typeof value === "string" &&
    (SPINE_SECTIONS as readonly string[]).includes(value)
  );
}

/**
 * Map runtime jumpIndex (+ heroPlayed) to a section id.
 * Prefer body[data-spine-section] when set by settleJump.
 * jumpIndex 1 is shared by hero (boot) and work (after heroEnter).
 */
export function resolveActiveSection(m: SiteMotion): SpineSection {
  const marked = m.body?.dataset?.spineSection;
  if (isSpineSection(marked)) return marked;
  const i = m.jumpIndex;
  if (i >= 5 || m.body.classList.contains("contact-active")) return "contact";
  if (i >= 4) return "story";
  if (i >= 3) return "products";
  if (i >= 2) return "services";
  if (i <= 0) return "hero";
  if (m.heroPlayed || (m.heroA?.progress() ?? 0) > 0.5) return "work";
  return "hero";
}

export function persistSpineSection(section: SpineSection): void {
  try {
    sessionStorage.setItem(SPINE_SECTION_STORAGE_KEY, section);
  } catch {
    /* private mode / quota — reload still proceeds without restore */
  }
}

export function peekPersistedSpineSection(): SpineSection | null {
  try {
    const raw = sessionStorage.getItem(SPINE_SECTION_STORAGE_KEY);
    return isSpineSection(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function clearPersistedSpineSection(): void {
  try {
    sessionStorage.removeItem(SPINE_SECTION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readSectionQuery(search: string): SpineSection | null {
  try {
    const value = new URLSearchParams(search).get("section");
    return isSpineSection(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Boot target: resize sessionStorage first (one-shot), else `?section=` deep link.
 * Does not clear storage — `seekToSpineSection` clears after land.
 */
export function resolveBootSection(
  search = typeof window !== "undefined" ? window.location.search : "",
): SpineSection | null {
  return peekPersistedSpineSection() ?? readSectionQuery(search);
}

function stripSectionQuery() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("section")) return;
    url.searchParams.delete("section");
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    /* ignore */
  }
}

/** ScrollSmoother + pin: use ScrollTrigger start/end, not getBoundingClientRect. */
export function jumpScrollTop(sid: string, frac: number) {
  if (sid === "_top") return 0;
  const byId =
    ScrollTrigger.getById(sid) || ScrollTrigger.getById(`${sid}-in`);
  if (byId && typeof byId.start === "number") {
    const end = typeof byId.end === "number" ? byId.end : byId.start;
    return byId.start + (end - byId.start) * Math.min(1, Math.max(0, frac));
  }
  const el = document.getElementById(sid);
  if (!el) return 0;
  return el.offsetTop + el.offsetHeight * frac;
}

/** Destination chrome after jump / restore seeks. */
export function applySectionTheme(m: SiteMotion, section: string) {
  const workLight = section === "work";
  if (workLight) {
    m.body.classList.add("work-scroll");
    gsap.set(m.body, {
      background: "#C7C7C7",
      color: "#121212",
      overwrite: "auto",
    });
  } else {
    m.body.classList.remove("work-scroll");
    gsap.set(m.body, {
      background: "#080808",
      color: "#FFF",
      overwrite: "auto",
    });
  }
  if (section === "contact") {
    m.body.classList.add("contact-active");
    document.getElementById("contact")?.classList.add("is-active");
  } else {
    m.body.classList.remove("contact-active");
    document.getElementById("contact")?.classList.remove("is-active");
  }
  m.body.dataset.spineSection = section;
}

/** Kill invisible full-bleed hit targets left mid-tween after a jump. */
export function clearJumpOverlays() {
  gsap.set([".site_overlay", ".menu_overlay"], {
    autoAlpha: 0,
    pointerEvents: "none",
    overwrite: "auto",
  });
}

function clearInertHash() {
  try {
    if (window.location.hash === "#" || window.location.href.endsWith("#")) {
      const { pathname, search } = window.location;
      window.history.replaceState({}, "", `${pathname}${search}`);
    }
  } catch {
    /* ignore */
  }
}

/** Waypoints for jump-menu / resize restore / `?section=` seeks. */
export function sectionJumpSteps(
  section: SpineSection,
): [string, number, number][] {
  if (section === "hero") return [["_top", 0.12, 0]];
  /*
   * Work intro sits at workTrigger start (scrub 0). Landing on heroTrigger@mid
   * sometimes overshoots into cases-in during seekMode, which snapped HeroB to
   * end and armed introCase — wrong destination after the overlay lifted.
   */
  if (section === "work") {
    return [
      ["heroTrigger", 0.06, 0.55],
      ["workTrigger", 0.12, 0],
    ];
  }
  if (section === "services") {
    return [
      ["servicesTrigger", 0.08, 1],
      ["services-in", 0.18, 0.55],
    ];
  }
  if (section === "products") {
    return [
      ["servicesTrigger", 0.06, 1],
      ["products-in", 0.2, 0.55],
    ];
  }
  if (section === "story") {
    return [
      ["servicesTrigger", 0.04, 1],
      ["products-in", 0.1, 0.55],
      ["story-in", 0.18, 0.55],
    ];
  }
  return [
    ["servicesTrigger", 0.04, 1],
    ["products-in", 0.08, 0.55],
    ["story-in", 0.1, 0.55],
    ["contact-in", 0.18, 0.55],
  ];
}

export function scrollSectionChain(
  m: SiteMotion,
  steps: [string, number, number][],
  done: () => void,
) {
  if (!m.smoother) return done();
  const tl = gsap.timeline({ onComplete: done });
  steps.forEach(([sid, pause, frac]) => {
    const top = jumpScrollTop(sid, frac);
    /*
     * Direct scrollTop while smoother may be paused (splash-lock). Tweening
     * scrollTop with paused(false) let the user drag the scrollbar mid-seek.
     */
    tl.call(() => {
      m.smoother?.scrollTop(top);
      ScrollTrigger.update();
    });
    if (pause > 0) tl.to({}, { duration: pause });
  });
  return tl;
}

export type SeekSpineOptions = {
  onDone?: () => void;
  /**
   * When false, leave ScrollSmoother locked after land (under-loader restore).
   * Default true for mid-session / post-reveal callers.
   */
  unlock?: boolean;
};

export type TransitionToSectionOptions = {
  onDone?: () => void;
  /** Fade `.site_overlay` during seek. Default true for mid-session jumps. */
  overlay?: boolean;
  /**
   * Full spine reset + epoch rebuild before seek. Default false —
   * normal nav only seeks + settleJump. Use true only for recovery.
   */
  rebuild?: boolean;
  /** Leave ScrollSmoother locked after land. Default true. */
  unlock?: boolean;
};

let transitionGen = 0;
let activeOverlayTl: gsap.core.Timeline | null = null;
let activeChainTl: gsap.core.Timeline | null = null;
let activeFillTween: gsap.core.Tween | null = null;

function releaseSeekLock(m: SiteMotion) {
  m.seekMode = false;
  m.scrollLocked = false;
  if (m.lockFailsafe) {
    clearTimeout(m.lockFailsafe);
    m.lockFailsafe = undefined;
  }
  m.body?.classList.remove("splash-lock");
  m.smoother?.paused(false);
}

function holdSeekLock(m: SiteMotion) {
  m.seekMode = true;
  m.scrollLocked = true;
  /* Pause user scroll; programmatic scrollTop in the chain still applies. */
  m.smoother?.paused(true);
  m.body?.classList.add("splash-lock");
  if (m.lockFailsafe) {
    clearTimeout(m.lockFailsafe);
    m.lockFailsafe = undefined;
  }
  /* Failsafe: never leave seekMode stuck if a chain callback is skipped. */
  m.lockFailsafe = setTimeout(() => {
    m.lockFailsafe = undefined;
    if (m.seekMode) releaseSeekLock(m);
  }, 8000);
}

/** Reset + run the outlined wavy fill on `.splash_mark` (fast — seeks are short). */
function playSplashFill() {
  const mark = document.querySelector<HTMLElement>(".site_overlay .splash_mark");
  if (!mark) return;
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  activeFillTween?.kill();
  activeFillTween = null;
  if (reduced) {
    mark.style.setProperty("--splash-fill", "1");
    mark.style.setProperty("--splash-wave", "0");
    return;
  }
  const state = { fill: 0, wave: 0 };
  mark.style.setProperty("--splash-fill", "0");
  mark.style.setProperty("--splash-wave", "0");
  activeFillTween = gsap.to(state, {
    fill: 1,
    wave: 1,
    duration: 0.52,
    ease: "power2.out",
    onUpdate: () => {
      mark.style.setProperty("--splash-fill", String(state.fill));
      mark.style.setProperty("--splash-wave", String(state.wave));
    },
    onComplete: () => {
      activeFillTween = null;
    },
  });
}

function closeJumpChrome(m: SiteMotion) {
  const menuSet = document.querySelector(".jump_menu_set");
  if (menuSet?.classList.contains("opened")) {
    menuSet.classList.remove("opened");
    m.body.classList.remove("jump-menu-open");
  }
  gsap.set(".menu_overlay", {
    autoAlpha: 0,
    pointerEvents: "none",
    overwrite: "auto",
  });
}

function finishTransition(
  m: SiteMotion,
  section: SpineSection,
  unlock: boolean,
  onDone?: () => void,
) {
  m.spine?.settleJump(section);
  if (unlock) releaseSeekLock(m);
  else {
    m.seekMode = false;
    m.scrollLocked = true;
    m.smoother?.paused(true);
    /* Keep splash-lock — caller is still under the boot loader. */
    m.body?.classList.add("splash-lock");
  }
  clearJumpOverlays();
  applySectionTheme(m, section);
  clearInertHash();
  m.caseIndex = 1;
  m.casePrev = 1;
  onDone?.();
}

/**
 * Single writer for mid-session + boot section lands.
 * Cancels any in-flight transition. Default path: overlay → seek waypoints →
 * settleJump (no full rebuild). Optional rebuild awaits `requestRebuildAsync`.
 */
export function transitionToSection(
  m: SiteMotion,
  section: SpineSection,
  options?: TransitionToSectionOptions,
) {
  const overlay = options?.overlay !== false;
  const rebuild = options?.rebuild === true;
  const unlock = options?.unlock !== false;
  const gen = ++transitionGen;

  activeOverlayTl?.kill();
  activeChainTl?.kill();
  activeFillTween?.kill();
  activeOverlayTl = null;
  activeChainTl = null;
  activeFillTween = null;

  const runChain = () => {
    if (gen !== transitionGen) return;
    holdSeekLock(m);
    ScrollTrigger.refresh();
    const chain = scrollSectionChain(m, sectionJumpSteps(section), () => {
      if (gen !== transitionGen) return;
      if (overlay) {
        activeOverlayTl = gsap
          .timeline()
          .to(".site_overlay", { autoAlpha: 0, duration: 0.22, ease: EASE_OUT }, 0)
          .set(".site_overlay", { pointerEvents: "none" })
          .call(() => {
            if (gen !== transitionGen) return;
            finishTransition(m, section, unlock, options?.onDone);
          });
      } else {
        finishTransition(m, section, unlock, options?.onDone);
      }
    });
    if (chain) activeChainTl = chain;
  };

  const afterChrome = () => {
    if (gen !== transitionGen) return;
    closeJumpChrome(m);
    if (rebuild && typeof m.requestRebuildAsync === "function") {
      void m.requestRebuildAsync().then(() => {
        if (gen !== transitionGen) return;
        runChain();
      });
      return;
    }
    runChain();
  };

  /* Block wheel/touch/scrollbar for the whole overlay path (incl. fade-in). */
  holdSeekLock(m);

  if (!overlay) {
    afterChrome();
    return;
  }

  activeOverlayTl = gsap
    .timeline()
    .set(".site_overlay", { pointerEvents: "auto" }, 0)
    .call(() => {
      const mark = document.querySelector<HTMLElement>(
        ".site_overlay .splash_mark",
      );
      if (!mark) return;
      mark.style.setProperty("--splash-fill", "0");
      mark.style.setProperty("--splash-wave", "0");
    }, undefined, 0)
    .to(".site_overlay", { autoAlpha: 1, duration: 0.22, ease: EASE_OUT })
    .call(playSplashFill, undefined, 0.06)
    .call(afterChrome);
}

/**
 * Instant spine land (no jump overlay / no rebuild). Used under the loader for
 * resize restore and `?section=` deep links. Clears persisted storage when done.
 * `settleJump("work")` → `settleWorkIntro` (intro, not case paint).
 */
export function seekToSpineSection(
  m: SiteMotion,
  section: SpineSection,
  options?: SeekSpineOptions,
) {
  const unlock = options?.unlock !== false;
  transitionToSection(m, section, {
    overlay: false,
    rebuild: false,
    unlock,
    onDone: () => {
      clearPersistedSpineSection();
      options?.onDone?.();
    },
  });
}

export type RestoreBootOptions = {
  onDone?: () => void;
  /** Keep lock after seek (loader still covering). Default false under-loader. */
  unlock?: boolean;
};

/**
 * Quiet-seek to resize sessionStorage or ADR-020 `?section=` while chrome may
 * still be covered. Strips the query param. Call before loader dismiss so the
 * first visible paint is already the target section (no hero flash/snap).
 */
export function restoreBootSection(
  m: SiteMotion,
  options?: RestoreBootOptions,
) {
  const fromQuery = readSectionQuery(window.location.search);
  const section = resolveBootSection();
  if (fromQuery) stripSectionQuery();

  if (!section || section === "hero") {
    clearPersistedSpineSection();
    m.spine?.settleJump("hero");
    applySectionTheme(m, "hero");
    options?.onDone?.();
    return;
  }

  const unlock = options?.unlock === true;
  const run = () => {
    ScrollTrigger.refresh();
    seekToSpineSection(m, section, {
      unlock,
      onDone: options?.onDone,
    });
  };

  /* Double rAF: JumpMenu bind + section slices arm on the same epoch tick. */
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}
